import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import GoldButton from '../components/GoldButton';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Shield, User } from 'lucide-react';

export default function AdminLogin() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('superadmin');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);

  useEffect(() => {
    api.get('/auth/me')
      .then(() => navigate('/admin/dashboard', { replace: true }))
      .catch(() => setCheckingSession(false));
  }, [navigate]);

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/auth/login-interno', { username, password });
      toast.success('Bienvenido!');
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Credenciales invalidas');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/admin/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (checkingSession) {
    return (
      <div className="min-h-screen bg-[#1A1A1A] flex items-center justify-center">
        <div className="text-[#F5A623] text-xl">Verificando sesion...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1A1A1A] flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <img
            src="https://customer-assets.emergentagent.com/job_cb23de51-57d5-4133-8f16-28731c09c0ec/artifacts/6u7zs9oj_KOMPA%204.png"
            alt="KOMPA Logo"
            className="w-32 h-32 mx-auto object-contain mb-4"
          />
          <h1 className="text-4xl text-gradient-kompa mb-2 font-bold">ADMINISTRACION</h1>
          <p className="text-white">La Meta - KOMPA</p>
        </div>

        <div className="glass-card rounded-2xl overflow-hidden" data-testid="admin-login-form">
          {/* Tabs */}
          <div className="flex border-b border-white/10">
            <button
              onClick={() => setActiveTab('superadmin')}
              className={`flex-1 flex items-center justify-center gap-2 py-4 text-sm font-semibold transition-colors ${
                activeTab === 'superadmin'
                  ? 'text-[#EF4444] border-b-2 border-[#EF4444] bg-[#EF4444]/5'
                  : 'text-[#666666] hover:text-white'
              }`}
              data-testid="tab-superadmin"
            >
              <Shield className="w-4 h-4" />
              Acceso Propietario
            </button>
            <button
              onClick={() => setActiveTab('admin')}
              className={`flex-1 flex items-center justify-center gap-2 py-4 text-sm font-semibold transition-colors ${
                activeTab === 'admin'
                  ? 'text-[#3B82F6] border-b-2 border-[#3B82F6] bg-[#3B82F6]/5'
                  : 'text-[#666666] hover:text-white'
              }`}
              data-testid="tab-admin"
            >
              <User className="w-4 h-4" />
              Acceso Administrativo
            </button>
          </div>

          <div className="p-8">
            {activeTab === 'superadmin' ? (
              <div className="space-y-6" data-testid="superadmin-tab-content">
                <div className="text-center">
                  <div className="w-20 h-20 mx-auto bg-[#EF4444]/10 rounded-full flex items-center justify-center mb-4">
                    <Shield className="w-10 h-10 text-[#EF4444]" />
                  </div>
                  <p className="text-white text-lg font-semibold mb-2">Acceso Propietario</p>
                  <p className="text-[#666666] text-sm">Inicia sesion con tu cuenta de Google autorizada</p>
                </div>

                <motion.button
                  onClick={handleGoogleLogin}
                  whileHover={{ scale: 1.02, boxShadow: "0 0 30px rgba(239, 68, 68, 0.3)" }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-white text-gray-800 rounded-xl font-bold text-base hover:bg-gray-50 transition-all shadow-lg"
                  data-testid="google-login-button"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Iniciar sesion con Google
                </motion.button>
              </div>
            ) : (
              <form onSubmit={handleAdminLogin} className="space-y-6" data-testid="admin-tab-content">
                <div className="text-center mb-2">
                  <div className="w-20 h-20 mx-auto bg-[#3B82F6]/10 rounded-full flex items-center justify-center mb-4">
                    <User className="w-10 h-10 text-[#3B82F6]" />
                  </div>
                </div>

                <div>
                  <Label htmlFor="username" className="text-white text-sm uppercase tracking-wider mb-2 block">
                    Usuario
                  </Label>
                  <Input
                    id="username"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    className="bg-[#2A2A2A] border border-white/20 text-white rounded-lg px-4 py-3 w-full focus:border-[#3B82F6] focus:ring-2 focus:ring-[#3B82F6]/20"
                    data-testid="username-input"
                  />
                </div>

                <div>
                  <Label htmlFor="password" className="text-white text-sm uppercase tracking-wider mb-2 block">
                    Contrasena
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="bg-[#2A2A2A] border border-white/20 text-white rounded-lg px-4 py-3 w-full focus:border-[#3B82F6] focus:ring-2 focus:ring-[#3B82F6]/20"
                    data-testid="password-input"
                  />
                </div>

                <GoldButton
                  type="submit"
                  variant="primary"
                  disabled={loading}
                  className="w-full"
                  testId="submit-login-button"
                >
                  {loading ? 'INGRESANDO...' : 'INGRESAR'}
                </GoldButton>
              </form>
            )}
          </div>
        </div>

        <div className="text-center mt-6">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="text-[#666666] hover:text-[#F5A623] text-sm transition-colors"
            data-testid="back-to-home-link"
          >
            Volver al inicio
          </button>
        </div>
      </motion.div>
    </div>
  );
}
